import { useEffect, useRef } from 'react';

import i18n from '@/shared/i18n';
import { getOnboardingToken } from '@/shared/api/storage';
import type { RegisterInvitationRequest } from '@/features/auth/types/invitation.types';

interface UseInitPasswordCreationParams {
  isFirstAccess: boolean;
  inviteId: string | null;
  token: string | null;
  existingUser: string | null;
  invitationAccepted: () => void;
  invitationFailed: (error: string) => void;
  reset: () => void;
  registerInvitation: (payload: RegisterInvitationRequest) => void;
}

/**
 * Initializes the password creation flow on mount.
 *
 * Handles two entry paths:
 * - **First-access**: Business Account owner arrived via onboarding email.
 *   JoinPage sets the `firstAccess` router state only after `/auth/identify`
 *   succeeded and its onboarding token was kept in memory. That token does
 *   not survive a page reload, in which case the link must be reopened.
 * - **Invite**: New user arrived via invitation link (URL query params).
 *
 * Manages the `hasCalledApi` guard internally to prevent duplicate calls
 * caused by React Strict Mode double-invocation.
 */
export function useInitPasswordCreation({
  isFirstAccess,
  inviteId,
  token,
  existingUser,
  invitationAccepted,
  invitationFailed,
  reset,
  registerInvitation,
}: UseInitPasswordCreationParams): void {
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (hasCalledApi.current) return;

    reset();

    if (isFirstAccess) {
      hasCalledApi.current = true;
      if (!getOnboardingToken()) {
        invitationFailed(
          i18n.t('Invalid invitation link. Missing or invalid params.')
        );
        return;
      }
      invitationAccepted();
      return;
    }

    if (!inviteId || !token || existingUser !== 'false') {
      invitationFailed(
        i18n.t('Invalid invitation link. Missing or invalid params.')
      );
      return;
    }

    hasCalledApi.current = true;
    registerInvitation({ inviteId, token });
    // Intentionally omitted: isFirstAccess (stable location.state),
    // invitationAccepted, invitationFailed, reset (stable Zustand actions),
    // registerInvitation (stable TanStack Query mutate).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUser, inviteId, token]);
}
